import React, { useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import BucketsInput from '../BucketsInput';
import ConditionBuilder from '../ConditionBuilder';
import TimePicker from '../TimePicker';
import LinkValue from './LinkValue';
import DropdownValue from './DropdownValue';
import MultiselectValue from './MultiselectValue';
import IncludeMultiselectValue from './IncludeMultiselectValue';
import NumberInputValue from './NumberInputValue';
import StaticTextValue from './StaticTextValue';
import ComparisonValue from './ComparisonValue';
import RangeValue from './RangeValue';
import IncludeExcludeMultiselectValue from './IncludeExcludeMultiselectValue';
import SearchInputValue from './SearchInputValue';
import { MAX_CONDITIONS_PER_FIELD } from '../../fieldsConfig';
import { useEligibilityActions } from '../../../../hooks/useEligibilityEngine';
import APP_PATH from '../../../../constants/paths';

const SA_LIGHT_OPTIONS = ['GREEN', 'YELLOW', 'RED'];

const ValueSelector = ({ rule }) => {
  const { onValueChange, onAddCondition } = useEligibilityActions();
  const disabled = !rule.enabled;
  const salesChannelOptionsRef = useRef([]);

  const salesChannelIdStrings = useMemo(() => {
    if (rule.id !== 'salesChannelEligibilityRule') return [];
    try {
      return (JSON.parse(rule.value)?.allowedChannelIds || []).map(String);
    } catch (e) {
      return [];
    }
  }, [rule.id, rule.value]);

  const mergedSalesChannelOptions = useMemo(
    () => [
      ...new Set([...salesChannelOptionsRef.current, ...salesChannelIdStrings]),
    ],
    [salesChannelIdStrings]
  );

  useEffect(() => {
    if (rule.id === 'salesChannelEligibilityRule') {
      salesChannelOptionsRef.current = mergedSalesChannelOptions;
    }
  }, [rule.id, mergedSalesChannelOptions]);

  switch (rule.id) {
    case 'odometerEligibilityRule':
    case 'priceEligibilityRule':
    case 'yearEligibilityRule':
    case 'isPriceInRange':
    case 'isMMRListed':
    case 'oveYearEligiblity':
    case 'mmrEligibilityRule':
    case 'saPurchasePriceEligibilityRule':
      return (
        <RangeValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={false}
        />
      );
    case 'saPricingPercentOfMmrRule': {
      const { minPct: min, maxPct: max } = JSON.parse(rule.value || '{}');
      return (
        <RangeValue
          rule={{ ...rule, value: JSON.stringify({ min, max }) }}
          onValueChange={(id, v) => {
            const { min: minPct, max: maxPct } = JSON.parse(v || '{}');
            onValueChange(id, JSON.stringify({ minPct, maxPct }));
          }}
          disabled={false}
        />
      );
    }
    case 'ageEligibilityRule': {
      const { max } = JSON.parse(rule.value || '{}');
      return (
        <NumberInputValue
          rule={{ ...rule, numberValue: max }}
          onValueChange={(id, _key, val) => onValueChange(id, JSON.stringify({ max: val }))}
        />
      );
    }
    case 'conditionReportEligibilityRule': {
      const { min } = JSON.parse(rule.value || '{}');
      return (
        <NumberInputValue
          rule={{ ...rule, numberValue: min }}
          onValueChange={(id, _key, val) => onValueChange(id, JSON.stringify({ min: val }))}
        />
      );
    }
    case 'saPricingDollarOverMmrRule': {
      const { maxDelta } = JSON.parse(rule.value || '{}');
      return (
        <NumberInputValue
          rule={{ ...rule, numberValue: maxDelta }}
          onValueChange={(id, _key, val) => onValueChange(id, JSON.stringify({ maxDelta: val }))}
        />
      );
    }
    case 'salesChannelEligibilityRule': {
      return (
        <MultiselectValue
          rule={{
            ...rule,
            options: mergedSalesChannelOptions,
            value: JSON.stringify(salesChannelIdStrings),
          }}
          onValueChange={(id, newValue) => {
            const selected = JSON.parse(newValue);
            onValueChange(
              id,
              JSON.stringify({ allowedChannelIds: selected.map(Number) })
            );
          }}
          disabled={false}
        />
      );
    }
    case 'saLightEligibilityRule': {
      const allowedColors = (() => {
        try {
          return JSON.parse(rule.value)?.allowedColors || [];
        } catch (e) {
          return [];
        }
      })();
      return (
        <MultiselectValue
          rule={{
            ...rule,
            options: SA_LIGHT_OPTIONS,
            value: JSON.stringify(allowedColors),
          }}
          onValueChange={(id, newValue) => {
            const selected = JSON.parse(newValue);
            onValueChange(id, JSON.stringify({ allowedColors: selected }));
          }}
          disabled={false}
        />
      );
    }
    case 'fuelTypeEligibilityRule': {
      const fuelTypes = (() => {
        try {
          return JSON.parse(rule.value)?.fuelTypes || [];
        } catch (e) {
          return [];
        }
      })();
      return (
        <MultiselectValue
          rule={{
            ...rule,
            options: fuelTypes.map(ft => ft.name),
            value: JSON.stringify(
              fuelTypes.filter(ft => ft.enabled).map(ft => ft.name)
            ),
          }}
          onValueChange={(id, newValue) => {
            const selected = JSON.parse(newValue);
            onValueChange(
              id,
              JSON.stringify({
                fuelTypes: fuelTypes.map(ft => ({
                  ...ft,
                  enabled: selected.includes(ft.name),
                })),
              })
            );
          }}
          disabled={false}
        />
      );
    }
    case 'lights':
    case 'vehicleLocation':
    case 'saleType':
    case 'inventorySource':
    case 'fuelType':
      return (
        <MultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'link':
      return (
        <LinkValue
          valueLinkText={rule.valueLinkText}
          valueLinkUrl={rule.valueLinkUrl}
        />
      );

    case 'dropdown':
      return (
        <DropdownValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'multiselect':
      return (
        <MultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'buckets':
      return (
        <BucketsInput
          buckets={rule.buckets}
          ruleId={rule.instanceId}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'include_multiselect':
      return (
        <IncludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'number_input':
      return (
        <NumberInputValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'crTypeEligibilityRule':
      return (
        <StaticTextValue staticText='include only Manheim-classified CRs' />
      );

    case 'traStatusEligibilityRule':
      return <StaticTextValue staticText='do not allow TRA' />;

    case 'saAnnouncementEligibilityRule':
      return (
        <StaticTextValue staticText='do not allow ineligibility keywords in announcements' />
      );

    case 'saPreviousTRAEligibilityRule':
      return (
        <StaticTextValue staticText='deny vehicle if previous TRA problem file is found' />
      );

    case 'crossoverEligibilityRule':
      return <StaticTextValue staticText='do not allow crossover' />;

    case 'oveManExEligibilityRule':
      return (
        <StaticTextValue staticText='do not allow OVE/ManEx ineligibility keywords' />
      );

    case 'ineligibleLaneEligibilityRule':
      // TODO: validate the destination link with the Product Owner and switch
      // back to <LinkValue> once the Ineligible Lanes page is available.
      return (
        <StaticTextValue staticText='automatically fetched from the Ineligible Lanes file' />
      );

    case 'vinBlacklistEligibilityRule':
      return (
        <LinkValue
          valueLinkText='automatically fetched from the VIN Blacklist, to edit go'
          valueLinkUrl={APP_PATH.VIN_BLACKLISTS}
        />
      );

    case 'businessUnitEligibilityRule':
      return (
        <StaticTextValue staticText='deny vehicle if prime code is T (TRA), H (heavy duties), or S (specialty car)' />
      );

    case 'static_text':
      return <StaticTextValue staticText={rule.staticText} />;

    case 'comparison_value': {
      // Calculate current condition count and effective max
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      const currentCount = rule.conditions?.length ?? 1;
      const canAddCondition = rule.allowAddCondition && currentCount < effectiveMax;

      return (
        <>
          <ComparisonValue
            rule={rule}
            onValueChange={onValueChange}
            disabled={disabled}
          />
          {canAddCondition && (
            <a
              href='#'
              className='add-condition-link'
              onClick={e => {
                e.preventDefault();
                onAddCondition(rule.instanceId);
              }}
              aria-label='Add condition'
              title='Add condition'
            >
              + Add condition
            </a>
          )}
        </>
      );
    }

    case 'range':
      return (
        <RangeValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'include_exclude_multiselect':
      return (
        <IncludeExcludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'condition_builder': {
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      return (
        <ConditionBuilder
          conditions={rule.conditions}
          ruleId={rule.instanceId}
          onValueChange={onValueChange}
          disabled={disabled}
          allowAddCondition={rule.allowAddCondition}
          maxConditions={effectiveMax}
          onAddCondition={onAddCondition}
        />
      );
    }

    case 'search_input':
      return (
        <SearchInputValue
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      );

    case 'time_picker':
      return (
        <TimePicker
          rule={rule}
          onValueChange={onValueChange}
          disabled={disabled}
          allowAddCondition={rule.allowAddCondition}
        />
      );

    case 'empty':
      return null;

    default:
      return null;
  }
};

ValueSelector.propTypes = {
  rule: PropTypes.object.isRequired,
};

export default ValueSelector;
